// Image filter utilities using sharp library for server-side processing
// For client-side, we'll use canvas-based implementations

/**
 * Canvas-based mosaic filter implementation for client-side use
 */
export const applyMosaicFilter = async (
  imageUrl: string,
  mosaicSize = 10
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }
      
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Draw original image
      ctx.drawImage(img, 0, 0);
      
      // Apply mosaic effect
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      for (let y = 0; y < canvas.height; y += mosaicSize) {
        for (let x = 0; x < canvas.width; x += mosaicSize) {
          // Get average color of the mosaic block
          let r = 0, g = 0, b = 0, count = 0;
          
          for (let dy = 0; dy < mosaicSize && y + dy < canvas.height; dy++) {
            for (let dx = 0; dx < mosaicSize && x + dx < canvas.width; dx++) {
              const index = ((y + dy) * canvas.width + (x + dx)) * 4;
              r += data[index];
              g += data[index + 1];
              b += data[index + 2];
              count++;
            }
          }
          
          // Calculate average
          r = Math.floor(r / count);
          g = Math.floor(g / count);
          b = Math.floor(b / count);
          
          // Fill the mosaic block with average color
          for (let dy = 0; dy < mosaicSize && y + dy < canvas.height; dy++) {
            for (let dx = 0; dx < mosaicSize && x + dx < canvas.width; dx++) {
              const index = ((y + dy) * canvas.width + (x + dx)) * 4;
              data[index] = r;
              data[index + 1] = g;
              data[index + 2] = b;
              // Alpha channel remains the same
            }
          }
        }
      }
      
      // Put the modified image data back
      ctx.putImageData(imageData, 0, 0);
      
      // Convert to data URL
      resolve(canvas.toDataURL('image/jpeg', 0.9));
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    img.src = imageUrl;
  });
};

/**
 * Canvas-based pixelate filter implementation
 */
export const applyPixelateFilter = async (
  imageUrl: string,
  pixelSize = 8
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }
      
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Disable image smoothing for pixelated effect
      ctx.imageSmoothingEnabled = false;
      
      // Draw image at smaller size
      const smallWidth = Math.ceil(img.width / pixelSize);
      const smallHeight = Math.ceil(img.height / pixelSize);
      
      ctx.drawImage(img, 0, 0, smallWidth, smallHeight);
      
      // Scale back up to original size
      ctx.drawImage(canvas, 0, 0, smallWidth, smallHeight, 0, 0, img.width, img.height);
      
      resolve(canvas.toDataURL('image/jpeg', 0.9));
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    img.src = imageUrl;
  });
};

/**
 * Apply various image filters based on filter type
 */
export const applyImageFilter = async (
  imageUrl: string,
  filterType: string,
  options: Record<string, unknown> = {}
): Promise<string> => {
  switch (filterType) {
    case 'mosaic':
      return applyMosaicFilter(imageUrl, options.mosaicSize || 10);
    case 'pixelate':
      return applyPixelateFilter(imageUrl, options.pixelSize || 8);
    default:
      return imageUrl;
  }
};

/**
 * Create a mosaic overlay canvas element for real-time preview
 */
export const createMosaicOverlay = (
  width: number,
  height: number,
  mosaicSize = 10,
  opacity = 0.5
): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    throw new Error('Canvas context not available');
  }
  
  canvas.width = width;
  canvas.height = height;
  
  // Create mosaic pattern
  ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
  
  for (let y = 0; y < height; y += mosaicSize) {
    for (let x = 0; x < width; x += mosaicSize) {
      // Create checkerboard pattern for mosaic effect
      if ((Math.floor(x / mosaicSize) + Math.floor(y / mosaicSize)) % 2 === 0) {
        ctx.fillRect(x, y, mosaicSize, mosaicSize);
      }
    }
  }
  
  return canvas;
};